import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, Camera, Package, AlertCircle, Zap, Building2, FileCheck, ArrowRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { integralSecurity } from '../data/mock';
import '../styles/pages.css';
import { soemLogo } from '../assets';
import usePageMeta from '../hooks/usePageMeta';

const iconMap = {
  MapPin, Users, Camera, Package, AlertCircle, Zap, Building2, FileCheck
};

// Imágenes de fondo para cada área de seguridad
const securityImages = {
  1: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80", // Contexto del Evento
  2: "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=800&q=80", // Seguridad Física
  3: "https://images.unsplash.com/photo-1558002038-1055907df827?w=800&q=80", // Seguridad Electrónica
  4: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&q=80", // Seguridad Patrimonial
  5: "https://images.unsplash.com/photo-1587745416684-47953f16f02f?w=800&q=80", // Manejo de Emergencias
  6: "https://images.unsplash.com/photo-1573167507387-6b4b98cb7c13?w=800&q=80", // Manejo de Crisis
  7: "https://images.unsplash.com/photo-1557862921-37829c790f19?w=800&q=80", // Coordinación con Autoridades
  8: "https://images.unsplash.com/photo-1582139329536-e7284fece509?w=800&q=80"  // Protección Civil
};

const IntegralSecurity = () => {
  usePageMeta({
    title: 'Seguridad Integral',
    description: 'Un modelo que integra protección civil, seguridad operativa y gestión de riesgos bajo un mismo criterio técnico.',
    path: '/integral-security',
  });

  return (
    <Layout>
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Seguridad Integral</h1>
          <p>Enfoque holístico que abarca todas las dimensiones de protección</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="section">
        <div className="container">
          <div className="intro-content">
            <p className="lead-text">
              La seguridad integral de un evento requiere considerar múltiples dimensiones 
              que interactúan entre sí. En SOEM Partners Group abordamos cada una de estas 
              áreas de manera coordinada para garantizar una protección efectiva.
            </p>
          </div>
        </div>
      </section>

      {/* Security Areas */}
      <section className="section section-alt">
        <div className="container">
          <div className="security-grid">
            {integralSecurity.map((area) => {
              const IconComponent = iconMap[area.icon];
              return (
                <div 
                  key={area.id} 
                  className="security-card-image"
                  style={{backgroundImage: `url(${securityImages[area.id]})`}}
                >
                  <div className="security-card-overlay"></div>
                  <div className="security-card-content">
                    <div className="security-icon">
                      {IconComponent && <IconComponent size={28} />}
                    </div>
                    <h3>{area.title}</h3>
                    <p>{area.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coordination */}
      <section className="section">
        <div className="container">
          <div className="content-grid">
            <div className="content-text">
              <h2>Coordinación Integral</h2>
              <p>
                La efectividad de la seguridad depende de la coordinación entre todas 
                sus dimensiones. Un incidente puede escalar rápidamente si no existe 
                comunicación fluida entre las diferentes áreas.
              </p>
              <p>
                Nuestro enfoque garantiza que todos los elementos trabajen de manera 
                sincronizada, con protocolos claros de comunicación y escalamiento, 
                y con roles y responsabilidades bien definidos.
              </p>
              <p>
                La coordinación con autoridades locales, servicios de emergencia y 
                protección civil es fundamental para una respuesta efectiva ante 
                cualquier contingencia.
              </p>
            </div>
            <div className="content-image">
              <div className="image-box">
                <img 
                  src={soemLogo} 
                  alt="SOEM Partners Group"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Proteja su evento de manera integral</h2>
            <p>
              Solicite una evaluación de seguridad integral para su próximo evento.
            </p>
            <Link to="/contact" className="btn btn-primary">
              Solicitar evaluación
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default IntegralSecurity;
